import { useNavigate, useParams } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useChat } from '@tanstack/ai-react'
import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { questionContent } from '../../shared/attachments.js'
import { stripMentions } from '../../shared/mentions.js'
import { Thread } from '../components/answer/Thread.jsx'
import { ChatHeader, MoreButton, ShareButton } from '../components/ChatHeader.jsx'
import { Composer } from '../components/composer/Composer.jsx'
import { Menu } from '../components/ui/Menu.jsx'
import { LoadingState } from '../components/ui/LoadingState.jsx'
import { RenameDialog } from '../components/ui/RenameDialog.jsx'
import { useToast } from '../components/ui/Toast.jsx'
import { isNotFound } from '../lib/api.js'
import { chatConnection, useHistoryLoad } from '../lib/chat-connection.js'
import { describeError, isStopped } from '../lib/chat-errors.js'
import { copyText } from '../lib/clipboard.js'
import { dayLabel } from '../lib/dates.js'
import { newShareToken, shareUrl } from '../lib/ids.js'
import { findInPages } from '../lib/queries/conversation-cache.js'
import {
  invalidateConversation,
  upsertConversationInLists,
  useConversation,
  useDeleteConversation,
  useRenameConversation,
  useRevokeShare,
  useSetFeedback,
  useShareConversation,
} from '../lib/queries/conversations.js'
import { qk } from '../lib/queries/keys.js'
import { useSystems, useSystemsMap } from '../lib/queries/systems.js'
import { useSaveWidgetState, useWidgetStates } from '../lib/queries/widgets.js'
import { isOptimisticConversation, peekQuestion, settleConversation, takeQuestion } from '../state/pending.js'
import p from './pages.module.css'

// One SSE connection for the app (lib/chat-connection.js): POST streams an
// answer, GET hydrates a conversation by threadId (server-authoritative).

export function ChatPage() {
  const { conversationId } = useParams({ strict: false })
  // A new mount per conversation gives each one its own chat client; a new
  // attempt after a failed history load remounts it to load again.
  const [attempt, setAttempt] = useState(0)
  return (
    <ChatView
      key={`${conversationId}:${attempt}`}
      conversationId={conversationId}
      onReloadHistory={() => setAttempt((n) => n + 1)}
    />
  )
}

function conversationFromLists(queryClient, id) {
  for (const [, data] of queryClient.getQueriesData({ queryKey: qk.conversationLists })) {
    const found = findInPages(data, id)
    if (found) return found
  }
  return null
}

function useStickToBottom(deps) {
  const ref = useRef(null)
  const stick = useRef(true)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onScroll = () => {
      stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 96
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [])
  useLayoutEffect(() => {
    const el = ref.current
    if (el && stick.current) el.scrollTop = el.scrollHeight
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
  return { ref, pin: () => (stick.current = true) }
}

function ChatView({ conversationId, onReloadHistory }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const toast = useToast()
  const systemsMap = useSystemsMap()
  const { data: systems = [] } = useSystems()
  const isNew = isOptimisticConversation(conversationId)
  const detail = useConversation(conversationId)

  const chat = useChat({
    threadId: conversationId,
    connection: chatConnection,
    persistence: true,
    onFinish: () => {
      settleConversation(conversationId)
      invalidateConversation(queryClient, conversationId)
    },
    onError: () => invalidateConversation(queryClient, conversationId),
  })

  // The first question typed on /new is sent once this view is mounted.
  // Deferred a tick so React's dev double-mount cannot send it twice.
  useEffect(() => {
    if (!peekQuestion(conversationId)) return
    const timer = setTimeout(() => {
      const pending = takeQuestion(conversationId)
      if (pending) {
        setLive(true)
        chat.sendMessage(questionContent(pending.text, pending.attachments))
      }
    }, 0)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId])

  const scroll = useStickToBottom([chat.messages])
  const historyLoad = useHistoryLoad(conversationId)
  // live: this view has sent something (so stored turn outcomes are stale);
  // stopped: the user pressed Stop on the newest answer.
  const [live, setLive] = useState(false)
  const [stopped, setStopped] = useState(false)

  const rename = useRenameConversation()
  const remove = useDeleteConversation()
  const share = useShareConversation(conversationId)
  const revoke = useRevokeShare(conversationId)
  const setFeedback = useSetFeedback(conversationId)
  // The conversation row exists once its detail loads (a new one, after the first answer).
  const widgetStates = useWidgetStates(conversationId, { enabled: Boolean(detail.data) })
  const saveWidget = useSaveWidgetState(conversationId)
  const [renaming, setRenaming] = useState(false)

  const listed = conversationFromLists(queryClient, conversationId)
  const conversation = detail.data?.conversation ?? listed
  const firstQuestion = chat.messages.find((m) => m.role === 'user')
  const title =
    conversation?.title ??
    (firstQuestion ? stripMentions(firstQuestion.parts.map((part) => part.content ?? '').join('')) : 'New question')
  const shareInfo = detail.data?.share ?? null

  const notFound = detail.isError && isNotFound(detail.error) && !isNew && chat.messages.length === 0
  if (notFound) {
    return (
      <div className={p.chat}>
        <ChatHeader title="Conversation not found" />
        <div className={p.centerState}>
          <h2>This conversation isn't available</h2>
          <p>It may have been deleted, or it belongs to someone else. Shared answers open from their share link.</p>
          <button type="button" className="btn btn-dark" onClick={() => navigate({ to: '/new' })}>
            Ask a new question
          </button>
        </div>
      </div>
    )
  }

  const onSubmit = ({ text, attachments = [] }) => {
    if (chat.isLoading) return false
    const now = Date.now()
    const base = conversation ?? { id: conversationId, title, titleSource: 'question', systems: [], createdAt: now }
    // Optimistic: bump to the top of the history.
    upsertConversationInLists(queryClient, {
      ...base,
      updatedAt: now,
      lastMessageAt: now,
    })
    scroll.pin()
    setLive(true)
    setStopped(false)
    chat.sendMessage(questionContent(text, attachments))
    return true
  }

  const copyShareLink = async () => {
    const token = shareInfo?.id ?? newShareToken()
    await copyText(shareUrl(token))
    toast.success('Link copied — anyone signed in at DCKAP can view this conversation.')
    if (!shareInfo) {
      share.mutate(
        { token },
        {
          onSuccess: ({ share: saved }) => {
            if (saved.id !== token) copyText(shareUrl(saved.id))
          },
        },
      )
    }
  }

  const menuItems = [
    { label: 'Rename', onSelect: () => setRenaming(true), disabled: !conversation },
    ...(shareInfo ? [{ label: 'Turn off share link', onSelect: () => revoke.mutate() }] : []),
    {
      label: 'Delete',
      danger: true,
      disabled: !conversation || chat.isLoading,
      onSelect: () => {
        remove.mutate({ id: conversationId, title })
        navigate({ to: '/new' })
      },
    },
  ]

  const retry = () => {
    setLive(true)
    setStopped(false)
    scroll.pin()
    chat.reload()
  }
  const stop = () => {
    setStopped(true)
    chat.stop()
  }

  // Opening an existing conversation: its history loads from the server.
  const empty = !isNew && chat.messages.length === 0 && !chat.isLoading
  const loadingHistory =
    empty && (historyLoad.status === 'loading' || (historyLoad.status === 'idle' && detail.isPending))
  const historyError = empty && historyLoad.status === 'error' ? describeError(historyLoad.error) : null
  // Pressing Stop is not an error; it shows as a note instead.
  const error = chat.error && chat.messages.length > 0 && !isStopped(chat.error) ? chat.error : null

  return (
    <div className={p.chat}>
      <ChatHeader
        title={title}
        subtitle={dayLabel(conversation?.createdAt)}
        actions={
          <>
            <ShareButton onClick={copyShareLink} disabled={!conversation && chat.messages.length === 0} />
            <Menu trigger={(props) => <MoreButton {...props} />} items={menuItems} />
          </>
        }
      />
      <div className={p.thread} ref={scroll.ref}>
        {loadingHistory ? (
          <LoadingState label="Loading conversation…" />
        ) : historyError ? (
          <div className={p.centerState} role="alert">
            <h2>{historyError.title}</h2>
            <p>{historyError.message}</p>
            <button type="button" className="btn btn-dark" onClick={onReloadHistory}>
              Try again
            </button>
          </div>
        ) : (
          <div className={p.column}>
            <Thread
              messages={chat.messages}
              turns={detail.data?.turns ?? []}
              systemsMap={systemsMap}
              streaming={chat.isLoading}
              error={error}
              stopped={stopped}
              live={live}
              onRetry={retry}
              feedback={detail.data?.feedback ?? {}}
              onRate={(messageId, rating) => setFeedback.mutate({ messageId, rating })}
              onAsk={(text) => onSubmit({ text })}
              widgetStates={widgetStates.data}
              onSaveWidget={saveWidget}
            />
          </div>
        )}
      </div>
      <Composer
        systems={systems}
        busy={chat.isLoading}
        onStop={stop}
        onSubmit={onSubmit}
      />
      <RenameDialog
        open={renaming}
        initialTitle={title}
        onClose={() => setRenaming(false)}
        onSave={(newTitle) => {
          setRenaming(false)
          if (newTitle && newTitle !== title) rename.mutate({ id: conversationId, title: newTitle })
        }}
      />
    </div>
  )
}
